import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'next/navigation'

import { INDEXER_GC_TIME, INDEXER_STALE_TIME } from '@/config/consts'
import { EULER_INDEXER_PROXY_URL } from '@/config/euler-contracts'
import { useAccount } from '@/domain/hooks/useAccount'

import type { EulerUserDetailResponse, EulerUserTransaction } from './types'

const EULER_ACTIVITY_API_URL = `${EULER_INDEXER_PROXY_URL}/v2/user`
const EULER_ACTIVITY_CHAIN_ID = '999'

export async function fetchEulerUserTransactions(address: string): Promise<EulerUserTransaction[]> {
  const url = new URL(EULER_ACTIVITY_API_URL, window.location.origin)
  url.searchParams.set('chainId', EULER_ACTIVITY_CHAIN_ID)
  url.searchParams.set('address', address)

  const response = await fetch(url.toString())
  if (!response.ok) {
    if (response.status === 404 || response.status === 400) {
      return []
    }

    throw new Error('Failed to fetch Euler user activity')
  }

  const data: EulerUserDetailResponse = await response.json()
  return data.transactions ?? []
}

/**
 * Hook to fetch Euler per-user transaction history.
 *
 * Data comes from the Euler indexer proxy, which returns all transactions in a
 * single request.
 */
export function useEulerActivity() {
  const searchParams = useSearchParams()
  // Intentional: `?address=` allows viewing any wallet's public on-chain
  // activity. Address is validated server-side in fetchEulerUserTransactions.
  const urlAddress = searchParams.get('address')
  const account = useAccount()
  const effectiveAddress = urlAddress || account

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['euler-indexer', 'user-transactions', effectiveAddress],
    queryFn: () => fetchEulerUserTransactions(effectiveAddress!),
    enabled: !!effectiveAddress,
    staleTime: INDEXER_STALE_TIME,
    gcTime: INDEXER_GC_TIME,
    refetchOnMount: false,
    retry: 1,
  })

  return {
    transactions: data ?? ([] as EulerUserTransaction[]),
    isLoading,
    isError,
    error,
  }
}
